import PATHS from '../../Routers/PATHS';
import { Consts } from '../../res';

const { EXAM_TYPES } = Consts;

/**
 * The header's links, in logical order — the first one renders nearest the
 * logo, which is the right-hand end in Arabic.
 *
 * Three of the four labels are the landing header's own keys rather than
 * copies under `v2.student`. The two bars carry the same words, and a second
 * set of keys is how they drift: someone edits "المناهج التعليمية" in one place
 * and a signed-in reader keeps seeing the old wording. Only "الاشتراكات" is
 * new, because the landing bar has no such link.
 *
 * The design letters the second one "المناهج التعليمة"; the shared key spells
 * it "المناهج التعليمية", which is the correct form and what the site already
 * says everywhere else.
 *
 * Two of these leave the redesigned area: the catalogue and the contact page
 * are landing routes, which `StudentStack` serves through `landingScreens` so
 * a signed-in reader gets the same rebuilt pages a visitor does. The
 * subscriptions link now goes through `studentScreens` for the same reason,
 * and lands on the old page or the new one according to its own flag.
 */
export const NAV_LINKS = [
    { key: 'home', labelKey: 'v2.nav.home', to: PATHS.USER_DASHBOARD.URL },
    { key: 'curricula', labelKey: 'v2.nav.curricula', to: PATHS.LANDING_CERTIFICATIONS.URL },
    { key: 'subscriptions', labelKey: 'v2.student.nav.subscriptions', to: PATHS.USER_SUBSCRIPTIONS.URL },
    { key: 'contact', labelKey: 'v2.nav.contact', to: PATHS.LANDING_CONTACTUS.URL },
];

/**
 * How many notifications the bell's dropdown shows before the reader has to
 * unroll it — what the design draws: five items and a "تحميل المزيد" line under
 * them. The dashboard's panel has no cap; it is a queue the reader empties.
 */
export const NOTIFICATIONS_IN_DROPDOWN = 5;

/**
 * Cards on one page of `/user/subscriptions` — the eight the design's frame
 * draws, which is four rows of the two-column grid.
 */
export const SUBSCRIPTIONS_PER_PAGE = 8;

/**
 * The course page's tabs, in the order the Arabic reader meets them — the first
 * sits at the right-hand end of the row. The first is also the page's default,
 * and the only one with no `?tab=`.
 */
export const COURSE_TABS = ['chapters', 'history', 'performance', 'resources'];

/** Rows on one page of the exam history — the six the "Exam History" frame draws. */
export const EXAMS_PER_PAGE = 6;

/**
 * The exam results report's thresholds, as percentages.
 *
 * `RESULT_GOOD_FROM` is where a chapter's badge turns from "أداء متوسط" to
 * "أداء جيد". Below the exam's own pass line it is "يحتاج مراجعة". The old
 * report drew its lines at 80 and 95 whatever the pass mark, and could tell a
 * chapter that had passed it needed "noticeable improvement".
 *
 * `GRADE_BANDS` is the word under the overall ring for an attempt that passed,
 * highest first. Below the last band it is "مقبول", and a fail is always
 * "دون المطلوب". The design draws "ممتاز" alone; the steps are the usual Arabic
 * grading scale.
 */
export const RESULT_GOOD_FROM = 80;

export const GRADE_BANDS = [
    { from: 90, key: 'excellent' },
    { from: 80, key: 'veryGood' },
    { from: 70, key: 'good' },
];

/**
 * The question banks' setup wizard — section 16370:191557 "Exam setup".
 *
 * One page serves the four banks; the route says which. The steps are the
 * stepper's three, in order.
 */
export const BANK_EXAM_TYPES = {
    [PATHS.USER_BANK_MCQS.URL]: EXAM_TYPES.MCQS,
    [PATHS.USER_BANK_ESSAY.URL]: EXAM_TYPES.ESSAY,
    [PATHS.USER_BANK_TBS.URL]: EXAM_TYPES.TBS,
    [PATHS.USER_BANK_FILE.URL]: EXAM_TYPES.FILE,
};

export const BANK_STEPS = ['topics', 'modes', 'settings'];

/** The cap on a bank exam whose exam sets none — the old wizard's figure. */
export const BANK_MAX_QUESTIONS = 500;

/** The three rows under "اختر نمط الأسئلة", each one of the start request's `only…Questions` flags. */
export const BANK_MODES = ['unanswered', 'answered', 'favourite'];

/**
 * The multiple-choice filters. `value` is what the API takes.
 *
 * A concept belongs to a difficulty and can only be ticked while that
 * difficulty is — the old wizard's rule, and why each concept names one.
 *
 * Listed in the order the Arabic reader meets them: theory, easy and
 * knowledge first, at the right. The frame lays these rows out left to right,
 * the way the English list reads, which puts "تقييم" before "معرفة" for an
 * Arabic reader; the same goes for the two order cards below.
 */
export const QUESTION_CATEGORIES = [
    { value: 'THEORY', key: 'theory' },
    { value: 'PRACTICAL', key: 'practical' },
];

export const QUESTION_DIFFICULTIES = [
    { value: 'EASY', key: 'easy' },
    { value: 'MID', key: 'medium' },
    { value: 'HARD', key: 'hard' },
];

export const QUESTION_CONCEPTS = [
    { value: 'KNOWLEDGE', key: 'knowledge', difficulty: 'EASY', descriptionKey: 'KnowledgeDesc' },
    { value: 'COMPREHENSION', key: 'comprehension', difficulty: 'EASY', descriptionKey: 'ComprehensionDesc' },
    { value: 'APPLICATION', key: 'application', difficulty: 'MID', descriptionKey: 'ApplicationDesc' },
    { value: 'ANALYSIS', key: 'analysis', difficulty: 'MID', descriptionKey: 'AnalysisDesc' },
    { value: 'SYNTHESIS', key: 'synthesis', difficulty: 'HARD', descriptionKey: 'SynthesisDesc' },
    { value: 'EVALUATION', key: 'evaluation', difficulty: 'HARD', descriptionKey: 'EvaluationDesc' },
];

export const BANK_ORDERS = [
    { value: 'STANDARD', key: 'standard' },
    { value: 'RANDOM', key: 'random' },
];
